/*
 * Copyright 2026 Sony Corporation
 */

/**
 * Undo, as whole-document snapshots.
 *
 * A scene document is small JSON, so a snapshot costs less than the inverse-op
 * machinery it replaces — and it cannot be wrong, which inverse ops regularly
 * are for anything that generates ids. Devplan §7.4 rule 6 makes undo a
 * requirement rather than a feature, so the simplest correct thing wins.
 */
export interface History<T> {
  past: readonly T[];
  present: T;
  future: readonly T[];
}

/** Deep enough for a session; a scene edited past this is being built, not explored. */
const MAX_DEPTH = 100;

export const initialHistory = <T>(present: T): History<T> => ({ past: [], present, future: [] });

/** A new present. The redo stack is dropped, as it describes a future that no longer follows. */
export const commit = <T>(history: History<T>, present: T): History<T> => ({
  past: [...history.past, history.present].slice(-MAX_DEPTH),
  present,
  future: [],
});

export const canUndo = <T>(history: History<T>): boolean => history.past.length > 0;
export const canRedo = <T>(history: History<T>): boolean => history.future.length > 0;

export const undo = <T>(history: History<T>): History<T> => {
  const previous = history.past.at(-1);
  if (previous === undefined) {
    return history;
  }
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
  };
};

export const redo = <T>(history: History<T>): History<T> => {
  const [next, ...rest] = history.future;
  if (next === undefined) {
    return history;
  }
  return { past: [...history.past, history.present], present: next, future: rest };
};
